import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Preconception.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSeedling,
  faCheck,
  faXmark,
  faCalendarDays,
  faStethoscope,
  faCircleQuestion,
  faArrowRight,
  faSpinner,
  faHeartPulse,
  faBookOpen,
} from '@fortawesome/free-solid-svg-icons';
import { Container } from 'react-bootstrap';
import BackButton from '../../../components/BackButton/BackButton';
import WHArticleList from '../../../components/WHArticleList/WHArticleList';
import womensHealthService, { CycleEntry } from '../../../services/womensHealthService';
import { useProfile } from '../../../context/ProfileContext';

type PreconceptionTab = 'dos-donts' | 'ovulation' | 'checkup' | 'faqs';

const Preconception: React.FC = () => {
  const { t } = useTranslation(['womensHealth', 'common']);
  const { activeProfile } = useProfile();
  const [searchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState<PreconceptionTab>(
    (searchParams.get('tab') as PreconceptionTab) || 'dos-donts'
  );
  const [cycleData, setCycleData] = useState<CycleEntry | null>(null);
  const [loadingCycle, setLoadingCycle] = useState(false);

  useEffect(() => {
    if (activeTab === 'ovulation') {
      loadCycleData();
    }
  }, [activeTab, activeProfile]);

  const loadCycleData = async () => {
    setLoadingCycle(true);
    try {
      const data = await womensHealthService.getCycleLatest();
      setCycleData(data);
    } catch {
      setCycleData(null);
    } finally {
      setLoadingCycle(false);
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

  const tabs = [
    { id: 'dos-donts' as PreconceptionTab, label: "Do's & Don'ts", icon: faCheck },
    { id: 'ovulation' as PreconceptionTab, label: 'Ovulation Calendar', icon: faCalendarDays },
    { id: 'checkup' as PreconceptionTab, label: 'Checkup Guide', icon: faStethoscope },
    { id: 'faqs' as PreconceptionTab, label: 'FAQs', icon: faCircleQuestion },
  ];

  /* ---- Tab: Do's & Don'ts ---- */
  const renderDosDonts = () => (
    <div className="wh-pre-dos-donts">
      <WHArticleList
        stage="pregnancy"
        contentType="dos_donts"
        variant="card"
        icon={faCheck}
        accentColor="#22c55e"
        getAccent={(row) => (/don'?ts?/i.test(row.title) ? '#ef4444' : undefined)}
        getIcon={(row) => (/don'?ts?/i.test(row.title) ? faXmark : undefined)}
      />
    </div>
  );

  /* ---- Tab: Ovulation Calendar ---- */
  const renderOvulationCalendar = () => {
    if (loadingCycle) {
      return (
        <div className="wh-pre-loading">
          <FontAwesomeIcon icon={faSpinner} spin className="wh-pre-loading-icon" />
          <p>Loading cycle data...</p>
        </div>
      );
    }

    if (!cycleData) {
      return (
        <div className="wh-pre-empty-state">
          <div className="wh-pre-empty-icon">
            <FontAwesomeIcon icon={faCalendarDays} />
          </div>
          <h3>No Cycle Data Found</h3>
          <p>Log your first cycle to see your ovulation calendar and fertile window predictions.</p>
          <a href="/womens-health/cycle-tracker" className="wh-pre-link-btn">
            <FontAwesomeIcon icon={faArrowRight} />
            Go to Cycle Tracker
          </a>
        </div>
      );
    }

    const lastPeriod = cycleData.last_period_start;
    const nextPeriod = cycleData.next_period_start;
    const ovulation = cycleData.ovulation_date;
    const fertileStart = cycleData.fertile_window_start;
    const fertileEnd = cycleData.fertile_window_end;

    return (
      <div className="wh-pre-ovulation">
        <div className="wh-pre-ov-cards">
          <div className="wh-pre-ov-card">
            <div className="wh-pre-ov-card-icon" style={{ background: '#fef2f2', color: '#ef4444' }}>
              <FontAwesomeIcon icon={faCalendarDays} />
            </div>
            <span className="wh-pre-ov-card-label">Last Period</span>
            <span className="wh-pre-ov-card-date">{formatDate(lastPeriod)}</span>
          </div>
          {nextPeriod && (
            <div className="wh-pre-ov-card">
              <div className="wh-pre-ov-card-icon" style={{ background: '#fdf2f8', color: '#ec4899' }}>
                <FontAwesomeIcon icon={faCalendarDays} />
              </div>
              <span className="wh-pre-ov-card-label">Next Period</span>
              <span className="wh-pre-ov-card-date">{formatDate(nextPeriod)}</span>
            </div>
          )}
          {ovulation && (
            <div className="wh-pre-ov-card wh-pre-ov-card-highlight">
              <div className="wh-pre-ov-card-icon" style={{ background: '#f0fdf4', color: '#22c55e' }}>
                <FontAwesomeIcon icon={faSeedling} />
              </div>
              <span className="wh-pre-ov-card-label">Ovulation Day</span>
              <span className="wh-pre-ov-card-date">{formatDate(ovulation)}</span>
            </div>
          )}
          {fertileStart && fertileEnd && (
            <div className="wh-pre-ov-card">
              <div className="wh-pre-ov-card-icon" style={{ background: '#fefce8', color: '#eab308' }}>
                <FontAwesomeIcon icon={faHeartPulse} />
              </div>
              <span className="wh-pre-ov-card-label">Fertile Window</span>
              <span className="wh-pre-ov-card-date">
                {formatDate(fertileStart)} - {formatDate(fertileEnd)}
              </span>
            </div>
          )}
        </div>

        {nextPeriod && ovulation && fertileStart && fertileEnd && (
          <div className="wh-pre-timeline-section">
            <h4 className="wh-pre-timeline-title">Cycle Timeline</h4>
            <div className="wh-pre-timeline-strip">
              <div className="wh-pre-timeline-segment wh-pre-tl-period">
                <span className="wh-pre-tl-label">Period</span>
              </div>
              <div className="wh-pre-timeline-segment wh-pre-tl-follicular">
                <span className="wh-pre-tl-label">Follicular</span>
              </div>
              <div className="wh-pre-timeline-segment wh-pre-tl-fertile">
                <span className="wh-pre-tl-label">Fertile Window</span>
              </div>
              <div className="wh-pre-timeline-segment wh-pre-tl-ovulation">
                <span className="wh-pre-tl-label">Ovulation</span>
              </div>
              <div className="wh-pre-timeline-segment wh-pre-tl-luteal">
                <span className="wh-pre-tl-label">Luteal</span>
              </div>
            </div>
            <div className="wh-pre-timeline-dates">
              <span>{formatDate(lastPeriod)}</span>
              <span>{formatDate(fertileStart)}</span>
              <span>{formatDate(ovulation)}</span>
              <span>{formatDate(nextPeriod)}</span>
            </div>
            <div className="wh-pre-timeline-legend">
              <div className="wh-pre-legend-item">
                <span className="wh-pre-legend-dot" style={{ background: '#f87171' }}></span>
                Period
              </div>
              <div className="wh-pre-legend-item">
                <span className="wh-pre-legend-dot" style={{ background: '#93c5fd' }}></span>
                Follicular
              </div>
              <div className="wh-pre-legend-item">
                <span className="wh-pre-legend-dot" style={{ background: '#86efac' }}></span>
                Fertile
              </div>
              <div className="wh-pre-legend-item">
                <span className="wh-pre-legend-dot" style={{ background: '#fbbf24' }}></span>
                Ovulation
              </div>
              <div className="wh-pre-legend-item">
                <span className="wh-pre-legend-dot" style={{ background: '#c4b5fd' }}></span>
                Luteal
              </div>
            </div>
          </div>
        )}

        <div className="wh-pre-ov-info">
          <p>
            <strong>Cycle Length:</strong> {cycleData.avg_cycle_length} days &nbsp;|&nbsp;
            <strong>Period Length:</strong> {cycleData.avg_period_length} days
          </p>
        </div>
      </div>
    );
  };

  /* ---- Tab: Checkup Guide ---- */
  const renderCheckupGuide = () => (
    <div className="wh-pre-checkup-guide">
      <WHArticleList
        stage="pregnancy"
        contentType="checkup_guide"
        variant="card"
        icon={faStethoscope}
        accentColor="#3b82f6"
      />
    </div>
  );

  /* ---- Tab: FAQs ---- */
  const renderFaqs = () => (
    <div className="wh-pre-faqs">
      <WHArticleList
        stage="pregnancy"
        contentType="faqs"
        variant="accordion"
        icon={faCircleQuestion}
        accentColor="#8b5cf6"
      />
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dos-donts':
        return renderDosDonts();
      case 'ovulation':
        return renderOvulationCalendar();
      case 'checkup':
        return renderCheckupGuide();
      case 'faqs':
        return renderFaqs();
      default:
        return null;
    }
  };

  return (
    <div className="wh-pre-page">
      <section className="wh-pre-banner">
        <Container>
          <BackButton />
          <div className="wh-pre-banner-content">
            <div className="wh-pre-banner-icon">
              <FontAwesomeIcon icon={faSeedling} />
            </div>
            <div>
              <h1 className="wh-pre-banner-title">{t('womensHealth:hub.preconception')}</h1>
              <p className="wh-pre-banner-subtitle">
                Prepare your body and mind for a healthy pregnancy journey
              </p>
            </div>
          </div>
        </Container>
      </section>

      <Container>
        <div className="wh-pre-tabs-wrapper">
          <div className="wh-pre-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`wh-pre-tab-btn${activeTab === tab.id ? ' active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <FontAwesomeIcon icon={tab.icon} className="wh-pre-tab-icon" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="wh-pre-content" key={activeTab}>
          {renderTabContent()}
        </div>
      </Container>
    </div>
  );
};

export default Preconception;
